import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import axios from 'axios';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import QuickStartErrorText from './common/errorText';
import PageController from './common/pageController';
import CompanyLogoUpload from './programCustomization/companyLogo';
import { DEFAULT_POLICY } from './programCustomization/defaultPolicy';

import { axiosInstanceForTenantAPI } from '../../../assets/utils/axiosInstance';
import {
  getFileExtension,
  isRESTAPIError,
  setS3UploadFormData,
} from '../../../assets/utils/helpers';
import {
  generateFileUploadErrorResponse,
  generateFileUploadToS3BucketErrorResponse,
} from '../../../assets/utils/responseFactory';

interface QuickstartProgramCustomizationProps {
  slug: string;
  screenNum: number;
  maxScreenNum: number;
  changeScreen: (num: number) => void;
}

const roundedGradientButton = {
  borderRadius: '100px',
  width: '164px',
  color: 'black',
  fontWeight: 500,
  fontSize: '20px',
  textTransform: 'none',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: '100px',
    padding: '2px', //ajust the padding to change the width of the border radius
    background: 'linear-gradient(270deg, #00CECF 0%, #2449FC 100%)',
    WebkitMask:
      'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
  },
  '&:hover': {
    backgroundImage: 'linear-gradient(270deg, #00CECF 0%, #2449FC 100%)',
    color: 'white',
  },
};

const QuickstartProgramCustomization = ({
  slug,
  screenNum,
  maxScreenNum,
  changeScreen,
}: QuickstartProgramCustomizationProps) => {
  const intl = useIntl();
  const [uploadedLogo, setUploadedLogo] = useState<File | null>(null);
  const [validationError, setValidationError] = useState<string>('');
  const [requestStatus, setRequestStatus] = useState({
    isLoading: false,
    success: false,
    error: false,
    text: '',
  });

  const GENERIC_ERROR_MESSAGE = intl.formatMessage({
    defaultMessage: 'Error happened. We\'re currently fixing it.',
  });

  const uploadFileStart = async (file: File) => {
    try {
      const { name } = file;
      const body = {
        file_name: name,
        file_type: getFileExtension(name),
      };
      const response = await axiosInstanceForTenantAPI(slug).post(
        '/tenant-logo/upload/start',
        body,
      );

      const data = response.data;
      return data;
    } catch (err: unknown) {
      const errorMessage = GENERIC_ERROR_MESSAGE;
      if (isRESTAPIError(err)) {
        const resp = generateFileUploadErrorResponse(err.response.status);
        setRequestStatus({
          isLoading: true,
          success: false,
          error: false,
          text: '',
        });
        throw new Error(resp);
      }
      setRequestStatus({
        isLoading: false,
        success: false,
        error: true,
        text: errorMessage,
      });
      throw new Error(errorMessage);
    }
  };

  const uploadFileActual = async (
    url: string,
    requestBody: FormData,
    name: string,
  ) => {
    try {
      const apiUrl = `${url}`;

      const response = await axios({
        method: 'post',
        url: apiUrl,
        data: requestBody,
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const data = response.data;
      return data;
    } catch (err: unknown) {
      const errorMessage = GENERIC_ERROR_MESSAGE;
      if (isRESTAPIError(err)) {
        const resp = generateFileUploadToS3BucketErrorResponse(
          err.response.status,
          name,
        );
        throw new Error(resp);
      }
      throw new Error(errorMessage);
    }
  };

  const uploadFileFinish = async (id: number, name: string) => {
    try {
      const body = {
        logo_id: id,
      };
      const response = await axiosInstanceForTenantAPI(slug).post(
        '/tenant-logo/upload/finish',
        body,
      );

      const data = response.data;
      return data;
    } catch (err: unknown) {
      const errorMessage = GENERIC_ERROR_MESSAGE;
      if (isRESTAPIError(err)) {
        const resp = generateFileUploadErrorResponse(err.response.status, name);
        throw new Error(resp);
      }
      throw new Error(errorMessage);
    }
  };

  const uploadFile = async (file: File) => {
    const body = await uploadFileStart(file);
    const { url, id = null, fields } = body;
    const requestBody = { ...fields, ...{ file } };
    const requestBodyForS3 = setS3UploadFormData(requestBody);
    await uploadFileActual(url, requestBodyForS3, file.name);
    await uploadFileFinish(id, file.name);
    return id;
  };

  const submitLogoAndPolicy = async (
    uploadPolicy: string,
    uploadLogo: number,
  ) => {
    try {
      const body = {
        policy_text: uploadPolicy,
        logo: uploadLogo,
      };

      const response = await axiosInstanceForTenantAPI(slug).patch('', body);

      const data = response;
      return data;
    } catch (err: unknown) {
      const errorMessage = GENERIC_ERROR_MESSAGE;
      if (isRESTAPIError(err)) {
        const resp = generateFileUploadErrorResponse(err.response.status);
        throw new Error(resp);
      }
      throw new Error(errorMessage);
    }
  };

  const generatePolicy = (basePolicy: string): string => {
    if (!basePolicy || basePolicy?.length === 0)
      return intl.formatMessage({ defaultMessage: 'No Policy Yet' });
    return basePolicy;
  };

  const setPage = (pageNum: number) => {
    changeScreen(pageNum);
  };

  const proceedButtonHandler = async () => {
    if (uploadedLogo === null) {
      setValidationError(
        intl.formatMessage({ defaultMessage: 'Please upload a logo' }),
      );
      return;
    } else setValidationError('');

    try {
      setRequestStatus({
        isLoading: true,
        success: false,
        error: false,
        text: '',
      });
      const fileidOfUploadedFile = await uploadFile(uploadedLogo);
      const logoAndPolicyData = await submitLogoAndPolicy(
        generatePolicy(DEFAULT_POLICY),
        fileidOfUploadedFile,
      );
      if (logoAndPolicyData.status === 200) {
        setRequestStatus({
          isLoading: false,
          success: true,
          error: false,
          text: '',
        });
        setPage(5);
      }
    } catch (err) {
      setRequestStatus({
        isLoading: false,
        success: false,
        error: true,
        text: `${err}`,
      });
    }
  };

  return (
    <Stack
      sx={{
        padding: '90px 64px',
        minHeight: '100vh',
        scrollSnapAlign: 'center',
      }}
    >
      <PageController
        page={screenNum}
        setPage={setPage}
        totalPage={maxScreenNum}
      />

      <Typography
        sx={{
          fontFamily: 'Schibsted Grotesk, sans-serif',
          fontSize: '28px',
          fontWeight: 700,
          color: '#181818',
          lineHeight: '150%',
          maxWidth: 650,
          marginBottom: '16px',
        }}
      >
        <FormattedMessage defaultMessage="Upload your company's logo to reflect your brand identity." />
      </Typography>
      <Stack gap={0.5} marginBottom={4}>
        <CompanyLogoUpload value={uploadedLogo} setValue={setUploadedLogo} />
        {validationError !== '' && (
          <QuickStartErrorText error={validationError} />
        )}
      </Stack>
      <Typography
        sx={{
          fontFamily: 'Schibsted Grotesk, sans-serif',
          fontSize: '16px',
          fontWeight: 400,
          color: '#181818',
          lineHeight: '125%',
          marginLeft: '180px',
          marginBottom: '64px',
        }}
      >
      </Typography>
      <Button sx={roundedGradientButton} onClick={() => proceedButtonHandler()}>
        {requestStatus.isLoading ? (
          <CircularProgress size={20} sx={{ marginY: 1 }} />
        ) : (
          intl.formatMessage({ defaultMessage: 'Proceed' })
        )}
      </Button>
    </Stack>
  );
};

export default QuickstartProgramCustomization;
